import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { isMobileDevice } from '../utils/deviceDetection';
import { switchPlayerMobile } from '../utils/mobilePlayerSwitching';
import { getMobileGainNode, fadeMobileGain, ensureAudioContextResumed, setMasterStereoMode, connectWaveSurferToStereoMode, disconnectWaveSurferFromStereoMode, setMasterFilter, updateWaveSurferFilterParameters, toggleMasterDim, getMasterDimState, remapMobileAudioConnections, cleanupMobileAudioForPlayer, type StereoMode, type FilterType, type FilterSlope } from '../utils/mobileAudioContextManager';
import { naturalSortNumbers } from '../utils/sorting';
import { synchronizeAllWaveforms } from '../utils/waveformSync';

interface PlayerLoopPoints {
  start: number;
  end: number;
}

interface AudioContextType {
  isPlaying: boolean;
  isLooping: boolean;
  currentTime: number;
  duration: number;
  selectedPlayer: number;
  gainOffsets: Map<number, number>;
  isBlindTestMode: boolean;
  blindTestMode: 'basic' | 'pick-fave' | 'guess-track' | null;
  blindTestIncludedPlayers: Map<number, boolean>;
  loopStart: number;
  loopEnd: number;
  loopState: 'inactive' | 'setting_start' | 'setting_end';
  playerLoopPoints: Map<number, PlayerLoopPoints>;
  lastStartPositions: Map<number, number>;
  setLastStartPositions: (positions: Map<number, number>) => void;
  isRandomSwitching: boolean;
  playAll: () => void;
  pauseAll: () => Promise<void>;
  stopAll: () => void;
  toggleLoop: () => void;
  forward: (seconds?: number) => Promise<void>;
  rewind: (seconds?: number) => Promise<void>;
  rewindToLastStart: () => Promise<void>;
  seekToTime: (time: number, syncPoints?: Map<number, number>, hasJumpedToSyncPoints?: boolean) => Promise<void>;
  selectPlayer: (playerNumber: number) => void;
  setGainOffsets: (offsets: Map<number, number>) => void;
  setGainOffsetsWithFileMapping: (offsets: Map<number, number>, fileOrder: Array<{id: string}>) => void;
  resetGainForPlayer: (playerNumber: number) => void;
  resetAllGains: () => void;
  toggleBlindTestMode: () => void;
  setBlindTestMode: (mode: 'basic' | 'pick-fave' | 'guess-track' | null) => void;
  toggleBlindTestIncluded: (playerNumber: number) => void;
  toggleBlindTestIncludedForSelected: () => void;
  toggleAllBlindTestIncluded: () => void;
  setLoopStart: (time: number, syncPoints?: Map<number, number>, hasJumpedToSyncPoints?: boolean) => void;
  setLoopEnd: (time: number, syncPoints?: Map<number, number>, hasJumpedToSyncPoints?: boolean) => void;
  disableLoop: () => void;
  toggleRandomSwitching: () => void;
  audioElements: Map<number, HTMLAudioElement>;
  registerAudio: (id: number, audio: HTMLAudioElement) => void;
  unregisterAudio: (id: number) => void;
  removePlayer: (id: number) => void;
  remapGainOffsets: (newFileOrder: Array<{id: string}>) => void;
  getPlayerLoopPoints: (playerNumber: number) => PlayerLoopPoints | null;
  stereoMode: StereoMode;
  toggleStereoMode: () => void;
  // Legacy compatibility
  isMono: boolean;
  toggleMono: () => void;
  resetMono: () => void;
  registerWaveSurfer: (playerNumber: number, wavesurfer: WaveSurfer) => void;
  // Filter controls
  filterType: FilterType;
  filterFrequency: number;
  filterQ: number;
  filterSlope: FilterSlope;
  filterFrequencyLow: number;
  filterFrequencyHigh: number;
  filterInputGain: number;
  setMasterFilter: (type: FilterType, frequency: number, q: number, slope?: FilterSlope, frequencyLow?: number, frequencyHigh?: number, inputGain?: number) => Promise<void>;
  resetMasterFilter: () => Promise<void>;
  toggleMasterFilter: () => Promise<void>;
  hasFilterBeenSet: boolean;
  // Dim controls
  isDimmed: boolean;
  toggleMasterDim: () => Promise<void>;
  // Waveform synchronization
  synchronizeWaveforms: (referencePlayer: number) => Promise<Map<number, number> | null>;
  // Spin animation for switcher button
  isSpinning: boolean;
  triggerSpinAnimation: () => void;
  // Allow repeats in blind test randomization
  blindTestAllowRepeats: boolean;
  setBlindTestAllowRepeats: (allow: boolean) => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

const DEBUG = false; // Enable for development

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration] = useState(0);
  const [selectedPlayer, setSelectedPlayer] = useState(1);
  const [isBlindTestMode, setIsBlindTestMode] = useState(false);
  const [blindTestMode, setBlindTestModeState] = useState<'basic' | 'pick-fave' | 'guess-track' | null>(null);
  const [loopStartTime, setLoopStartTime] = useState(0);
  const [loopEndTime, setLoopEndTime] = useState(0);
  const [loopState, setLoopState] = useState<'inactive' | 'setting_start' | 'setting_end'>('inactive');
  const [playerLoopPoints, setPlayerLoopPoints] = useState(new Map<number, PlayerLoopPoints>());
  const [lastStartPositions, setLastStartPositions] = useState(new Map<number, number>());
  const [audioElements] = useState(new Map<number, HTMLAudioElement>());
  const [gainOffsets, setGainOffsetsState] = useState(new Map<number, number>());
  const [blindTestIncludedPlayers, setBlindTestIncludedPlayers] = useState(new Map<number, boolean>());
  const [isRandomSwitching, setIsRandomSwitching] = useState(() => {
    const stored = localStorage.getItem('isRandomSwitching');
    return stored === null ? true : stored === 'true';
  });
  const [stereoMode, setStereoMode] = useState<StereoMode>('stereo');
  const [filterType, setFilterType] = useState<FilterType>('off');
  const [filterFrequency, setFilterFrequency] = useState<number>(1000);
  const [filterQ, setFilterQ] = useState<number>(0.707);
  const [filterSlope, setFilterSlope] = useState<FilterSlope>(12);
  const [filterFrequencyLow, setFilterFrequencyLow] = useState<number>(500);
  const [filterFrequencyHigh, setFilterFrequencyHigh] = useState<number>(2000);
  const [filterInputGain, setFilterInputGain] = useState<number>(1.0);
  
  // Store last active filter settings for toggle functionality
  const [lastActiveFilter, setLastActiveFilter] = useState<{
    type: FilterType;
    frequency: number;
    q: number;
    slope: FilterSlope;
    frequencyLow: number;
    frequencyHigh: number;
    inputGain: number;
  } | null>(null);
  
  // Dim state
  const [isDimmed, setIsDimmed] = useState<boolean>(false);
  
  // Spin animation state for switcher button
  const [isSpinning, setIsSpinning] = useState<boolean>(false);
  
  // Trigger spin animation
  const triggerSpinAnimation = useCallback(() => {
    setIsSpinning(true);
    setTimeout(() => setIsSpinning(false), 500);
  }, []);
  
  // Allow repeats in blind test randomization
  const [blindTestAllowRepeats, setBlindTestAllowRepeats] = useState<boolean>(false);
  
  // Reset allow repeats when blind test mode changes
  useEffect(() => {
    setBlindTestAllowRepeats(false);
  }, [blindTestMode]);
  
  // Ensure stereo mode is initialized on mount
  useEffect(() => {
    // Ensure mobile audio context starts in stereo mode on mount
    if (isMobileDevice()) {
      setMasterStereoMode('stereo').catch(error => {
        console.warn('Failed to initialize stereo mode:', error);
      });
    }
  }, []); // Only run on mount
  
  // Legacy compatibility: isMono derived from stereoMode
  const isMono = stereoMode === 'mono';
  
  // Use refs for values that should persist across renders
  const gainOffsetsRef = useRef(new Map<number, number>());
  const originalGainOffsetsRef = useRef(new Map<string, number>()); // Use file ID as key instead of player number
  const fileIdToGainOffsetRef = useRef(new Map<string, number>());
  const playerNumberToFileIdRef = useRef(new Map<number, string>()); // Track player number to file ID mapping
  const fadeLock = useRef<Promise<void>>(Promise.resolve());
  const isInitializedRef = useRef(false);
  const wavesurferRefs = useRef<Map<number, WaveSurfer>>(new Map());
  const visualSyncAnimationFrameRef = useRef<number | null>(null);
  const playingAudioRef = useRef<HTMLAudioElement | null>(null); // Track which audio is actually playing in iframe mode

  // Helper function to log debug information
  const debugLog = useCallback((message: string, data?: Record<string, unknown>) => {
    if (DEBUG) {
      console.log(`[AudioContext] ${message}`, data || '');
    }
  }, []);

  // Helper function to start/restart visual sync loop for iframe mode
  const startVisualSyncLoop = useCallback(() => {
    // Stop any existing visual sync loop
    if (visualSyncAnimationFrameRef.current !== null) {
      cancelAnimationFrame(visualSyncAnimationFrameRef.current);
      visualSyncAnimationFrameRef.current = null;
    }

    const syncVisualProgress = () => {
      // Get the current playing audio from ref (handles player switches)
      const playingAudio = playingAudioRef.current;
      
      // If no playing audio ref, try to find a playing audio element
      // This handles the case where playingAudioRef hasn't been updated yet during a switch
      if (!playingAudio || playingAudio.paused) {
        // Check if there's another audio element that's actually playing
        let foundPlayingAudio: HTMLAudioElement | null = null;
        for (const audio of audioElements.values()) {
          if (!audio.paused) {
            // In iframe mode, we check for any playing audio, not just volume > 0
            // because the new audio might be at volume 0 during fade-in
            foundPlayingAudio = audio;
            playingAudioRef.current = audio;
            break;
          }
        }
        
        // If still no playing audio, stop syncing
        if (!foundPlayingAudio) {
          visualSyncAnimationFrameRef.current = null;
          playingAudioRef.current = null;
          return;
        }
      }
      
      // Get the current playing audio (may have been updated above)
      const currentPlayingAudio = playingAudioRef.current;
      if (!currentPlayingAudio) {
        visualSyncAnimationFrameRef.current = null;
        return;
      }
      
      const playingTime = currentPlayingAudio.currentTime;
      
      // Sync all players' currentTime and WaveSurfer visuals
      // IMPORTANT: Don't modify the playing audio's currentTime or WaveSurfer - that causes glitches!
      // Only update the non-playing players and WaveSurfer visuals
      for (const [playerNumber, audio] of audioElements.entries()) {
        const isPlayingAudio = audio === currentPlayingAudio;
        
        // Only update currentTime for non-playing players (visual sync)
        // Never touch the playing audio's currentTime - it updates naturally
        if (!isPlayingAudio) {
          audio.currentTime = playingTime;
        }
        
        // Update WaveSurfer visual progress for ALL players (including the playing one for visual continuity)
        // But be careful: only update the playing player's WaveSurfer if it's not causing issues
        const wavesurfer = wavesurferRefs.current.get(playerNumber);
        if (wavesurfer) {
          try {
            const duration = audio.duration || wavesurfer.getDuration() || 1;
            if (duration > 0 && !isNaN(duration)) {
              const progress = Math.min(Math.max(playingTime / duration, 0), 1);
              // For non-playing players, seekTo updates visual progress
              // For playing player, we skip seekTo to avoid glitches, but the visual should update naturally
              if (!isPlayingAudio) {
                try {
                  wavesurfer.seekTo(progress);
                } catch (seekError) {
                  // Silently ignore seek errors - this is just visual sync
                }
              }
            }
          } catch (error) {
            // Ignore seek errors - this is just visual sync
          }
        }
      }
      
      // Continue syncing while playing
      const stillPlaying = currentPlayingAudio && !currentPlayingAudio.paused;
      if (stillPlaying) {
        visualSyncAnimationFrameRef.current = requestAnimationFrame(syncVisualProgress);
      } else {
        visualSyncAnimationFrameRef.current = null;
        playingAudioRef.current = null;
      }
    };
    
    // Start visual sync loop
    visualSyncAnimationFrameRef.current = requestAnimationFrame(syncVisualProgress);
  }, [audioElements]);

  // Helper function to apply gain to an audio element
  const applyGainToAudio = useCallback((audio: HTMLAudioElement, playerNumber: number) => {
    const gainOffset = gainOffsetsRef.current.get(playerNumber) || 1;
    const isSelected = playerNumber === selectedPlayer;
    
    // When selected, use the gain offset; otherwise set to 0
    const finalVolume = isSelected ? gainOffset : 0;
    audio.volume = Math.max(0, Math.min(1, finalVolume));
    
    debugLog(`Applied gain to player ${playerNumber}`, { gainOffset, isSelected, finalVolume });
  }, [selectedPlayer]);

  const fadeVolume = useCallback(async (audio: HTMLAudioElement, targetVolume: number, duration: number, playerNumber: number) => {
    const startVolume = audio.volume;
    const startTime = performance.now();
    
    return new Promise<void>((resolve) => {
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        const eased = (1 - Math.cos(progress * Math.PI)) / 2;
        const newVolume = startVolume + (targetVolume - startVolume) * eased;
        
        audio.volume = Math.max(0, Math.min(1, newVolume));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          audio.volume = targetVolume;
          resolve();
        }
      };
      
      requestAnimationFrame(animate);
    });
  }, []);

  const selectPlayer = useCallback(async (playerNumber: number) => {
    // Trigger spin animation for pick-fave and guess-track modes (automatic switches)
    // Always trigger it, even if same player is selected, to avoid exposing repeat selections
    if (isBlindTestMode && (blindTestMode === 'pick-fave' || blindTestMode === 'guess-track')) {
      triggerSpinAnimation();
    }
    
    // Don't do anything if selecting the already selected player
    if (playerNumber === selectedPlayer) return;
    
    await fadeLock.current;
    
    const currentAudio = audioElements.get(selectedPlayer);
    const newAudio = audioElements.get(playerNumber);
    const isInIframe = window.self !== window.top;
    const wasPlaying = isPlaying;
    
    if (currentAudio && newAudio) {
      debugLog(`Switching from player ${selectedPlayer} to ${playerNumber}`);
      
      // For iframes: if playing, we need to switch players cleanly
      // Since only one audio can play at a time in iframes, we pause old, start new, then fade in
      if (isInIframe && wasPlaying) {
        // Get the current playback time before pausing
        const syncTime = currentAudio.currentTime;
        
        // Sync the new player's time to match BEFORE starting playback
        // This ensures smooth transition without audio jump or glitch
        newAudio.currentTime = syncTime;
        
        // Set new audio volume to 0 initially (will fade in)
        const newTargetVolume = gainOffsetsRef.current.get(playerNumber) || 1;
        newAudio.volume = 0;
        
        const fadeDuration = 150; // 150ms fade
        
        try {
          // First, pause the old audio cleanly to avoid stuttering
          // Don't fade it - just pause it immediately since it can't play anyway
          currentAudio.pause();
          
          // Small delay to ensure pause is processed before starting new audio
          await new Promise(resolve => requestAnimationFrame(resolve));
          
          // Update the playing audio ref to the new player
          // This ensures visual sync continues smoothly
          playingAudioRef.current = newAudio;
          
          // Start the new audio playing (at volume 0)
          await newAudio.play();
          
          // Restart visual sync loop with the new playing audio
          // This ensures waveform animation continues on all players after switching
          startVisualSyncLoop();
          
          // Now fade in the new player's volume smoothly
          // Only fade the new audio - the old one is already paused
          fadeLock.current = fadeVolume(newAudio, newTargetVolume, fadeDuration, playerNumber).then(() => {
            console.log(`[AudioContext] Switched to player ${playerNumber} in iframe mode at time ${syncTime.toFixed(3)} with fade`);
            // Visual sync loop is already running with the new playing audio
          });
          
          await fadeLock.current;
          
          // Update selected player state after successful switch
          setSelectedPlayer(playerNumber);
        } catch (error) {
          console.error(`[AudioContext] Failed to start player ${playerNumber} after switch:`, error);
          playingAudioRef.current = currentAudio; // Revert to old audio if new one fails
          // Try to resume old audio if new one failed
          if (currentAudio.paused && wasPlaying) {
            currentAudio.play().catch(() => {});
          }
        }
        return; // Exit early for iframe mode - don't execute mobile/desktop switching
      }
      
      if (isMobileDevice()) {
        // Ensure audio context is resumed before switching on mobile
        await ensureAudioContextResumed();
        
        // Use WebAudio-based mobile switching
        await switchPlayerMobile({
          audioElements,
          selectedPlayer: playerNumber,
          gainOffsets: gainOffsetsRef.current,
          isBlindTestMode,
          isPlaying
        });
        setSelectedPlayer(playerNumber);
      } else {
        // Use desktop gain node switching
        const newGain = gainOffsetsRef.current.get(playerNumber) || 1;
        
        fadeLock.current = Promise.all([
          fadeVolume(currentAudio, 0, 20, selectedPlayer),
          fadeVolume(newAudio, newGain, 20, playerNumber)
        ]).then(() => {
          setSelectedPlayer(playerNumber);
        });
        
        await fadeLock.current;
      }
    }
  }, [selectedPlayer, audioElements, fadeVolume, isBlindTestMode, blindTestMode, isPlaying, startVisualSyncLoop, triggerSpinAnimation]);

  const registerAudio = useCallback((id: number, audio: HTMLAudioElement) => {
    console.log(`[AudioContext] Registering audio for player ${id}`, { 
      src: audio.src?.substring(0, 50) + '...',
      readyState: audio.readyState,
      paused: audio.paused
    });
    audioElements.set(id, audio);
    
    // Set initial volume based on selection state and gain offsets
    const isSelected = id === selectedPlayer;
    const gainOffset = gainOffsetsRef.current.get(id) || 1;
    const initialVolume = isSelected ? gainOffset : 0;
    audio.volume = initialVolume;
    audio.loop = isLooping;
    
    debugLog(`Registered audio for player ${id}`, { 
      isSelected, 
      gainOffset, 
      initialVolume 
    });

    // For mobile devices, set the initial gain value based on whether this player is selected
    if (isMobileDevice()) {
      // Set volume asynchronously to ensure gain nodes are properly set up
      setTimeout(async () => {
        try {
          const targetVolume = isSelected ? gainOffset : 0;
          
          debugLog(`Setting initial mobile gain for newly registered player ${id}`, { 
            isSelected, 
            gainOffset, 
            targetVolume 
          });
          
          await fadeMobileGain(id, targetVolume, 0);
        } catch (error) {
          console.warn(`Failed to set initial gain for newly registered player ${id}:`, error);
        }
      }, 100); // Small delay to ensure WebAudio context is fully initialized
    }
    
    // Auto-include new players in blind test mode
    setBlindTestIncludedPlayers(prev => {
      const newMap = new Map(prev);
      if (!newMap.has(id)) {
        newMap.set(id, true); // Default to included
      }
      return newMap;
    });
  }, [audioElements, isLooping, selectedPlayer]);

  const stopAll = useCallback(() => {
    audioElements.forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
    setIsPlaying(false);
    setCurrentTime(0);
  }, [audioElements]);

  // Helper function to wait for all audio elements to be ready (seeked/buffered)
  const waitForAudioElementsReady = useCallback(async (
    audioElements: Map<number, HTMLAudioElement>,
    targetTime: number,
    timeout: number = 500
  ): Promise<void> => {
    const startTime = performance.now();
    console.log('[MobileSync] waitForAudioElementsReady started', {
      targetTime: targetTime.toFixed(3),
      timeout,
      elementStates: Array.from(audioElements.entries()).map(([num, audio]) => ({
        player: num,
        currentTime: audio.currentTime.toFixed(3),
        readyState: audio.readyState,
        needsSeek: Math.abs(audio.currentTime - targetTime) > 0.01
      }))
    });
    
    return new Promise((resolve) => {
      const seekedSet = new Set<number>();
      const seekedHandlers = new Map<number, () => void>();
      const canplayHandlers = new Map<number, () => void>();
      
      let timeoutId: NodeJS.Timeout | null = null;
      let isResolved = false;
      
      const doResolve = () => {
        if (isResolved) return;
        isResolved = true;
        const elapsed = performance.now() - startTime;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        cleanup();
        console.log('[MobileSync] waitForAudioElementsReady resolved', {
          elapsed: elapsed.toFixed(2) + 'ms',
          readyPlayers: Array.from(seekedSet),
          finalStates: Array.from(audioElements.entries()).map(([num, audio]) => ({
            player: num,
            currentTime: audio.currentTime.toFixed(3),
            readyState: audio.readyState
          }))
        });
        resolve();
      };
      
      const checkAllReady = () => {
        const allReady = Array.from(audioElements.keys()).every(playerNumber => {
          const audio = audioElements.get(playerNumber);
          if (!audio) return true;
          
          const needsSeek = Math.abs(audio.currentTime - targetTime) > 0.01;
          const hasSeeked = seekedSet.has(playerNumber);
          const isAtTarget = !needsSeek && audio.readyState >= 2; // HAVE_CURRENT_DATA
          
          return hasSeeked || isAtTarget;
        });
        
        if (allReady) {
          doResolve();
        }
      };
      
      const cleanup = () => {
        audioElements.forEach((audio, playerNumber) => {
          const seekedHandler = seekedHandlers.get(playerNumber);
          const canplayHandler = canplayHandlers.get(playerNumber);
          if (seekedHandler) {
            audio.removeEventListener('seeked', seekedHandler);
          }
          if (canplayHandler) {
            audio.removeEventListener('canplay', canplayHandler);
          }
        });
      };
      
      // Set up listeners for each element
      audioElements.forEach((audio, playerNumber) => {
        const needsSeek = Math.abs(audio.currentTime - targetTime) > 0.01;
        
        if (needsSeek) {
          // Wait for seeked event
          const seekedHandler = () => {
            const elapsed = performance.now() - startTime;
            console.log(`[MobileSync] Player ${playerNumber} seeked event fired`, {
              elapsed: elapsed.toFixed(2) + 'ms',
              currentTime: audio.currentTime.toFixed(3),
              targetTime: targetTime.toFixed(3)
            });
            seekedSet.add(playerNumber);
            checkAllReady();
          };
          audio.addEventListener('seeked', seekedHandler);
          seekedHandlers.set(playerNumber, seekedHandler);
          console.log(`[MobileSync] Waiting for seeked event on player ${playerNumber}`, {
            currentTime: audio.currentTime.toFixed(3),
            targetTime: targetTime.toFixed(3)
          });
        } else {
          // Already at target, check readyState
          if (audio.readyState >= 2) {
            seekedSet.add(playerNumber);
            console.log(`[MobileSync] Player ${playerNumber} already at target, readyState=${audio.readyState}`);
          } else {
            // Wait for canplay
            const canplayHandler = () => {
              const elapsed = performance.now() - startTime;
              console.log(`[MobileSync] Player ${playerNumber} canplay event fired`, {
                elapsed: elapsed.toFixed(2) + 'ms',
                readyState: audio.readyState
              });
              seekedSet.add(playerNumber);
              checkAllReady();
            };
            audio.addEventListener('canplay', canplayHandler);
            canplayHandlers.set(playerNumber, canplayHandler);
            console.log(`[MobileSync] Waiting for canplay event on player ${playerNumber}`, {
              currentTime: audio.currentTime.toFixed(3),
              readyState: audio.readyState
            });
          }
        }
      });
      
      // Initial check
      checkAllReady();
      
      // Timeout fallback
      timeoutId = setTimeout(() => {
        if (!isResolved) {
          const elapsed = performance.now() - startTime;
          console.warn('[MobileSync] Timeout waiting for audio elements to be ready, proceeding anyway', {
            elapsed: elapsed.toFixed(2) + 'ms',
            timeout,
            readyPlayers: Array.from(seekedSet),
            pendingPlayers: Array.from(audioElements.keys()).filter(p => !seekedSet.has(p))
          });
          doResolve();
        }
      }, timeout);
    });
  }, []);

  const playAll = useCallback(async () => {
    // Ensure audio context is resumed before playing
    // This is needed for mobile devices and also for iframe embeds (which may have suspended contexts)
    await ensureAudioContextResumed();
    
    // Also ensure WaveSurfer's audio contexts are resumed (for WebAudio backend)
    // This is critical for iframe embeds where contexts may be suspended
    for (const [playerNumber, wavesurfer] of wavesurferRefs.current.entries()) {
      try {
        const wavesurferAny = wavesurfer as any;
        // Try to resume WaveSurfer's audio context if it exists
        if (wavesurferAny?.backend?.ac) {
          if (wavesurferAny.backend.ac.state === 'suspended') {
            await wavesurferAny.backend.ac.resume();
            console.log(`[AudioContext] Resumed WaveSurfer audio context for player ${playerNumber}`);
          }
          // Also check if the context got suspended again (iframe issue)
          if (wavesurferAny.backend.ac.state === 'suspended') {
            console.warn(`[AudioContext] WaveSurfer audio context for player ${playerNumber} is still suspended after resume attempt`);
            // Try one more time
            try {
              await wavesurferAny.backend.ac.resume();
              console.log(`[AudioContext] Retried resuming WaveSurfer audio context for player ${playerNumber}`);
            } catch (retryError) {
              console.warn(`[AudioContext] Failed to resume WaveSurfer audio context for player ${playerNumber} on retry:`, retryError);
            }
          }
        }
      } catch (error) {
        console.warn(`[AudioContext] Failed to resume WaveSurfer audio context for player ${playerNumber}:`, error);
      }
    }
    
    // For iframe embeds, also try to resume any audio element contexts
    // Some browsers create separate contexts for audio elements in iframes
    audioElements.forEach((audio, playerNumber) => {
      try {
        // Try to access the audio element's context if it exists
        const audioAny = audio as any;
        if (audioAny.audioContext && audioAny.audioContext.state === 'suspended') {
          audioAny.audioContext.resume().then(() => {
            console.log(`[AudioContext] Resumed audio element context for player ${playerNumber}`);
          }).catch((err: Error) => {
            console.warn(`[AudioContext] Failed to resume audio element context for player ${playerNumber}:`, err);
          });
        }
      } catch (error) {
        // Ignore - not all audio elements have accessible contexts
      }
    });
    
    // Add a small delay to ensure audio contexts are fully ready
    // This is especially important for the first play after loading files and in iframes
    await new Promise(resolve => setTimeout(resolve, 100));
    
    console.log('[AudioContext] Starting playAll', { 
      audioElementsCount: audioElements.size,
      selectedPlayer,
      isMobile: isMobileDevice(),
      audioElementKeys: Array.from(audioElements.keys())
    });
    
    if (audioElements.size === 0) {
      console.warn('[AudioContext] No audio elements registered! Cannot play.');
      return;
    }
    
    // Don't set playing state until we're sure playback will start
    const playPromises: Promise<void>[] = [];
    
    // For iframes, we need to resume contexts and play within user interaction
    // Resume all contexts first and wait for them to complete
    const resumePromises: Array<Promise<void>> = [];
    const contextMap = new Map<number, AudioContext>();
    
    audioElements.forEach((audio, playerNumber) => {
      const wavesurfer = wavesurferRefs.current.get(playerNumber);
      if (wavesurfer) {
        const wavesurferAny = wavesurfer as any;
        // Try multiple ways to access the audio context
        let audioContext: AudioContext | null = null;
        
        if (wavesurferAny?.backend?.ac) {
          audioContext = wavesurferAny.backend.ac;
        } else if (wavesurferAny?.ac) {
          audioContext = wavesurferAny.ac;
        } else if (wavesurferAny?.getMediaElement?.()?.audioContext) {
          audioContext = wavesurferAny.getMediaElement().audioContext;
        }
        
        if (audioContext) {
          contextMap.set(playerNumber, audioContext);
          console.log(`[AudioContext] Found audio context for player ${playerNumber}, state: ${audioContext.state}`);
          if (audioContext.state === 'suspended') {
            // Call resume() immediately - this must happen in user interaction event
            // Wrap in try-catch to ensure promise is always created
            try {
              const resumePromise = audioContext.resume().then(() => {
                const newState = audioContext!.state;
                console.log(`[AudioContext] Resumed WaveSurfer context for player ${playerNumber}, new state: ${newState}`);
                if (newState === 'suspended') {
                  console.warn(`[AudioContext] Context for player ${playerNumber} still suspended after resume()!`);
                }
              }).catch((error) => {
                console.error(`[AudioContext] Failed to resume context for player ${playerNumber}:`, error);
                console.error(`[AudioContext] Error details:`, {
                  errorMessage: error?.message,
                  errorName: error?.name,
                  errorStack: error?.stack
                });
              });
              resumePromises.push(resumePromise);
              console.log(`[AudioContext] Started resume() for player ${playerNumber}`);
            } catch (error) {
              console.error(`[AudioContext] Error calling resume() for player ${playerNumber}:`, error);
              // Create a rejected promise so Promise.allSettled doesn't hang
              resumePromises.push(Promise.reject(error));
            }
          } else {
            console.log(`[AudioContext] Audio context for player ${playerNumber} is already ${audioContext.state}`);
          }
        } else {
          console.warn(`[AudioContext] Could not find audio context for player ${playerNumber}`, {
            hasBackend: !!wavesurferAny?.backend,
            hasAC: !!wavesurferAny?.backend?.ac,
            hasMedia: !!wavesurferAny?.getMediaElement
          });
        }
      }
    });
    
    // Wait for resume() calls to complete, but with a short timeout
    // In iframes, we need to proceed quickly to stay within user interaction window
    if (resumePromises.length > 0) {
      try {
        // Race between resume completion and a timeout
        // Don't wait too long - if resume() hangs, proceed anyway
        const resumeResults = await Promise.race([
          Promise.allSettled(resumePromises),
          new Promise<PromiseSettledResult<void>[]>(resolve => {
            setTimeout(() => {
              console.warn('[AudioContext] Resume operations timed out, proceeding anyway');
              resolve(resumePromises.map(() => ({ status: 'fulfilled' as const, value: undefined })));
            }, 50); // Very short timeout - resume should be instant in user interaction
          })
        ]);
        
        console.log('[AudioContext] Resume operations completed', {
          total: resumePromises.length,
          fulfilled: resumeResults.filter(r => r.status === 'fulfilled').length,
          rejected: resumeResults.filter(r => r.status === 'rejected').length
        });
        
        // Log any rejections
        resumeResults.forEach((result, index) => {
          if (result.status === 'rejected') {
            const playerNumber = Array.from(contextMap.keys())[index];
            console.error(`[AudioContext] Resume failed for player ${playerNumber}:`, result.reason);
          }
        });
        
        // Verify all contexts are actually running - this is critical
        contextMap.forEach((context, playerNumber) => {
          if (context.state === 'suspended') {
            console.error(`[AudioContext] CRITICAL: Context for player ${playerNumber} is still suspended after resume attempts! Audio will be silent.`);
            // Try one more synchronous resume attempt
            try {
              context.resume().then(() => {
                console.log(`[AudioContext] Final resume attempt succeeded for player ${playerNumber}, state: ${context.state}`);
              }).catch((err) => {
                console.error(`[AudioContext] Final resume attempt failed for player ${playerNumber}:`, err);
              });
            } catch (err) {
              console.error(`[AudioContext] Error in final resume attempt for player ${playerNumber}:`, err);
            }
          } else {
            console.log(`[AudioContext] Context for player ${playerNumber} is ${context.state}`);
          }
        });
      } catch (error) {
        console.warn('[AudioContext] Error waiting for resume operations:', error);
      }
    }
    
    // Check if we're in an iframe
    const isInIframe = window.self !== window.top;
    
    // On mobile (non-iframe), ensure tracks are synced before playing
    if (isMobileDevice() && !isInIframe) {
      const referenceAudio = audioElements.get(selectedPlayer);
      if (referenceAudio) {
        const targetTime = referenceAudio.currentTime || 0;
        const beforeTimes = new Map<number, number>();
        audioElements.forEach((audio, num) => {
          beforeTimes.set(num, audio.currentTime);
        });
        
        // Check if tracks are already synced
        const timeValues = Array.from(beforeTimes.values());
        const maxTime = Math.max(...timeValues);
        const minTime = Math.min(...timeValues);
        const timeDiff = maxTime - minTime;
        
        if (timeDiff > 0.01) {
          // Tracks are not synced, sync them now
          console.log('[MobileSync] playAll: Tracks not synced, syncing before play', {
            targetTime: targetTime.toFixed(3),
            beforeTimes: Object.fromEntries(Array.from(beforeTimes.entries()).map(([n, t]) => [n, t.toFixed(3)]))
          });
          
          audioElements.forEach(audio => {
            audio.currentTime = targetTime;
          });
          
          // Wait for seeks to complete
          await waitForAudioElementsReady(audioElements, targetTime, 500);
          
          const afterTimes = new Map<number, number>();
          audioElements.forEach((audio, num) => {
            afterTimes.set(num, audio.currentTime);
          });
          console.log('[MobileSync] playAll: Sync complete before play', {
            afterTimes: Object.fromEntries(Array.from(afterTimes.entries()).map(([n, t]) => [n, t.toFixed(3)]))
          });
        } else {
          console.log('[MobileSync] playAll: Tracks already synced, proceeding to play', {
            targetTime: targetTime.toFixed(3),
            times: Object.fromEntries(Array.from(beforeTimes.entries()).map(([n, t]) => [n, t.toFixed(3)]))
          });
        }
      }
    }
    
    // Track play call timing for sync analysis (mobile only, non-iframe)
    const playCallStartTimes = new Map<number, number>();
    
    // For iframes, browsers only allow ONE play() call per user gesture
    // Workaround: Only play the selected player, but sync visual progress on all players
    // This makes it LOOK like all players are playing, but only the selected one makes sound
    if (isInIframe) {
      console.log(`[AudioContext] Iframe mode: Playing only selected player ${selectedPlayer} (visual sync for others)`);
      
      // Only play the selected player - this is the only one that will work in iframes
      const selectedAudio = audioElements.get(selectedPlayer);
      if (selectedAudio) {
        console.log(`[AudioContext] Attempting to play selected player ${selectedPlayer}`, {
          readyState: selectedAudio.readyState,
          paused: selectedAudio.paused,
          currentTime: selectedAudio.currentTime,
          duration: selectedAudio.duration,
          muted: selectedAudio.muted,
          volume: selectedAudio.volume,
          src: selectedAudio.src?.substring(0, 50) + '...'
        });
        
        // Play only the selected player
        const playPromise = selectedAudio.play().then(() => {
          console.log(`[AudioContext] Selected player ${selectedPlayer} started playing (iframe mode)`);
          
          // Store reference to the playing audio element
          playingAudioRef.current = selectedAudio;
          
          // Start visual sync loop for all players
          startVisualSyncLoop();
        }).catch((error) => {
          console.error(`[AudioContext] Failed to start playback for selected player ${selectedPlayer}:`, error);
          console.error(`[AudioContext] Audio element ${selectedPlayer} error details:`, {
            readyState: selectedAudio.readyState,
            paused: selectedAudio.paused,
            src: selectedAudio.src?.substring(0, 50) + '...',
            error: error instanceof Error ? error.message : String(error)
          });
          throw error;
        });
        
        playPromises.push(playPromise);
      } else {
        console.warn(`[AudioContext] Selected player ${selectedPlayer} not found in audioElements`);
      }
    } else {
      // For non-iframes, use WaveSurfer if available, otherwise use audio element
      for (const [playerNumber, audio] of audioElements.entries()) {
        const playCallStart = performance.now();
        playCallStartTimes.set(playerNumber, playCallStart);
        
        console.log(`[AudioContext] Attempting to play audio element ${playerNumber}`, {
          readyState: audio.readyState,
          paused: audio.paused,
          currentTime: audio.currentTime.toFixed(3),
          duration: audio.duration,
          muted: audio.muted,
          volume: audio.volume,
          src: audio.src?.substring(0, 50) + '...',
          callTime: playCallStart.toFixed(2) + 'ms'
        });
        
        const wavesurfer = wavesurferRefs.current.get(playerNumber);
        let playPromise: Promise<void>;
        
        if (wavesurfer) {
          // Use WaveSurfer's play() method
          playPromise = wavesurfer.play().then(() => {
            const resolveTime = performance.now();
            const callDuration = resolveTime - playCallStart;
            console.log(`[AudioContext] WaveSurfer play() succeeded for player ${playerNumber}`, {
              callDuration: callDuration.toFixed(2) + 'ms',
              currentTime: audio.currentTime.toFixed(3)
            });
          }).catch((error) => {
            console.warn(`[AudioContext] WaveSurfer play() failed for player ${playerNumber}, trying audio element:`, error);
            return audio.play();
          });
        } else {
          // Use audio element directly
          playPromise = audio.play().then(() => {
            const resolveTime = performance.now();
            const callDuration = resolveTime - playCallStart;
            console.log(`[AudioContext] Audio element play() succeeded for player ${playerNumber}`, {
              callDuration: callDuration.toFixed(2) + 'ms',
              currentTime: audio.currentTime.toFixed(3)
            });
          });
        }
        
        playPromise = playPromise.catch((error) => {
          console.error(`[AudioContext] Failed to start playback for player ${playerNumber}:`, error);
          console.error(`[AudioContext] Audio element ${playerNumber} error details:`, {
            readyState: audio.readyState,
            paused: audio.paused,
            src: audio.src?.substring(0, 50) + '...',
            error: error instanceof Error ? error.message : String(error)
          });
          throw error;
        });
        
        playPromises.push(playPromise);
      }
    }
    
    try {
      // Wait for all play attempts and see which ones succeeded
      const results = await Promise.allSettled(playPromises);
      const successCount = results.filter(result => result.status === 'fulfilled').length;
      const failureCount = results.filter(result => result.status === 'rejected').length;
      
      // Log playback state for debugging
      const afterPlayTimes = new Map<number, number>();
      audioElements.forEach((audio, playerNumber) => {
        afterPlayTimes.set(playerNumber, audio.currentTime);
        console.log(`[AudioContext] Audio element ${playerNumber} state after play()`, {
          paused: audio.paused,
          readyState: audio.readyState,
          readyStateText: typeof audio.readyState === 'number' ? ['HAVE_NOTHING', 'HAVE_METADATA', 'HAVE_CURRENT_DATA', 'HAVE_FUTURE_DATA', 'HAVE_ENOUGH_DATA'][audio.readyState] || 'UNKNOWN' : 'UNDEFINED',
          volume: audio.volume,
          muted: audio.muted,
          currentTime: audio.currentTime.toFixed(3),
          ended: audio.ended
        });
      });
      
      console.log('[AudioContext] PlayAll results', { 
        successCount, 
        failureCount, 
        totalAttempts: playPromises.length
      });
      
      // Log sync analysis for mobile
      if (isMobileDevice() && !isInIframe && afterPlayTimes.size > 1) {
        const timeValues = Array.from(afterPlayTimes.values());
        const maxTime = Math.max(...timeValues);
        const minTime = Math.min(...timeValues);
        const timeDiff = maxTime - minTime;
        
        // Calculate play call timing differences (only available for non-iframe)
        let playCallTimingInfo: any = 'Not available';
        if (playCallStartTimes.size > 0) {
          const playCallArray = Array.from(playCallStartTimes.entries()).map(([num, time]) => ({ num, time }));
          const firstCall = playCallArray[0];
          const callDiffs = playCallArray.slice(1).map(({ num, time }) => ({
            player: num,
            diffFromFirst: (time - firstCall.time).toFixed(2) + 'ms'
          }));
          playCallTimingInfo = {
            firstCall: `Player ${firstCall.num} at ${firstCall.time.toFixed(2)}ms`,
            otherCalls: callDiffs,
            totalCalls: playCallArray.length
          };
        }
        
        console.log('[MobileSync] playAll: Sync analysis', {
          playCallTiming: playCallTimingInfo,
          currentTimeSync: {
            times: Object.fromEntries(Array.from(afterPlayTimes.entries()).map(([n, t]) => [n, t.toFixed(3)])),
            maxDiff: timeDiff.toFixed(3) + 's',
            isSynced: timeDiff < 0.01
          }
        });
      }
      
      // Set playing state if at least one play() call succeeded
      // This matches the main app behavior - trust that play() working means playback started
      if (successCount > 0) {
        setIsPlaying(true);
        console.log('[AudioContext] Set isPlaying to true - at least one player started successfully');
        
        // Track the current positions as the last start positions
        const newStartPositions = new Map<number, number>();
        audioElements.forEach((audio, playerNumber) => {
          newStartPositions.set(playerNumber, audio.currentTime);
        });
        setLastStartPositions(newStartPositions);
        debugLog('Updated last start positions', Object.fromEntries(newStartPositions));
      } else {
        console.warn('[AudioContext] All play attempts failed - not setting isPlaying state');
        // Log the specific errors
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.warn(`[AudioContext] Player ${Array.from(audioElements.keys())[index]} play failed:`, result.reason);
          }
        });
      }
    } catch (error) {
      console.error('Unexpected error in playAll:', error);
    }
  }, [audioElements, selectedPlayer, startVisualSyncLoop, waitForAudioElementsReady]);

  const pauseAll = useCallback(async () => {
    audioElements.forEach(audio => audio.pause());
    setIsPlaying(false);
    
    // Stop visual sync animation frame if running (for iframe mode)
    if (visualSyncAnimationFrameRef.current !== null) {
      cancelAnimationFrame(visualSyncAnimationFrameRef.current);
      visualSyncAnimationFrameRef.current = null;
    }
    playingAudioRef.current = null;

    // On mobile, sync all tracks to the same time immediately after pause
    if (isMobileDevice()) {
      const referenceAudio = audioElements.get(selectedPlayer);
      if (referenceAudio) {
        const targetTime = referenceAudio.currentTime || 0;
        
        console.log('[MobileSync] pauseAll: Syncing tracks after pause', {
          targetTime: targetTime.toFixed(3),
          beforeTimes: Object.fromEntries(Array.from(audioElements.entries()).map(([n, a]) => [n, a.currentTime.toFixed(3)]))
        });
        
        // Sync all elements to same time
        audioElements.forEach(audio => {
          audio.currentTime = targetTime;
        });
        
        // Wait for seeks to complete (if any occurred)
        await waitForAudioElementsReady(audioElements, targetTime, 500);
        
        console.log('[MobileSync] pauseAll: Sync complete', {
          afterTimes: Object.fromEntries(Array.from(audioElements.entries()).map(([n, a]) => [n, a.currentTime.toFixed(3)]))
        });
      }
    }
  }, [audioElements, selectedPlayer, waitForAudioElementsReady]);

  const setLoopStart = useCallback((time: number, syncPoints?: Map<number, number>, hasJumpedToSyncPoints?: boolean) => {
    setLoopStartTime(time);
    setLoopState('setting_end');
    
    // Calculate displaced loop start points for each player when sync is active
    if (syncPoints && hasJumpedToSyncPoints && syncPoints.size > 1) {
      const referenceSyncPoint = syncPoints.get(selectedPlayer);
      if (referenceSyncPoint !== undefined) {
        const referenceOffset = time - referenceSyncPoint;
        const newPlayerLoopPoints = new Map<number, PlayerLoopPoints>();
        
        audioElements.forEach((audio, playerNumber) => {
          const playerSyncPoint = syncPoints.get(playerNumber);
          if (playerSyncPoint !== undefined) {
            const displacedStart = playerSyncPoint + referenceOffset;
            // Keep existing end point if it exists, otherwise set to 0
            const existingPoints = playerLoopPoints.get(playerNumber);
            newPlayerLoopPoints.set(playerNumber, {
              start: Math.max(0, Math.min(displacedStart, audio.duration || Infinity)),
              end: existingPoints?.end || 0
            });
          }
        });
        
        setPlayerLoopPoints(newPlayerLoopPoints);
      }
    } else {
      // Clear player-specific loop points when sync is not active
      setPlayerLoopPoints(new Map());
    }
  }, [selectedPlayer, audioElements, playerLoopPoints]);

  const setLoopEnd = useCallback((time: number, syncPoints?: Map<number, number>, hasJumpedToSyncPoints?: boolean) => {
    if (time > loopStartTime) {
      setLoopEndTime(time);
      setIsLooping(true);
      setLoopState('inactive');
      
      // Calculate displaced loop end points for each player when sync is active
      if (syncPoints && hasJumpedToSyncPoints && syncPoints.size > 1) {
        const referenceSyncPoint = syncPoints.get(selectedPlayer);
        if (referenceSyncPoint !== undefined) {
          const referenceOffset = time - referenceSyncPoint;
          const newPlayerLoopPoints = new Map<number, PlayerLoopPoints>();
          
          audioElements.forEach((audio, playerNumber) => {
            const playerSyncPoint = syncPoints.get(playerNumber);
            if (playerSyncPoint !== undefined) {
              const displacedEnd = playerSyncPoint + referenceOffset;
              // Get existing start point or use the displaced start
              const existingPoints = playerLoopPoints.get(playerNumber);
              const displacedStart = existingPoints?.start || playerSyncPoint + (loopStartTime - referenceSyncPoint);
              newPlayerLoopPoints.set(playerNumber, {
                start: displacedStart,
                end: Math.max(0, Math.min(displacedEnd, audio.duration || Infinity))
              });
            }
          });
          
          setPlayerLoopPoints(newPlayerLoopPoints);
        }
      }
    }
  }, [loopStartTime, selectedPlayer, audioElements, playerLoopPoints]);

  const disableLoop = useCallback(() => {
    setIsLooping(false);
    setLoopStartTime(0);
    setLoopEndTime(0);
    setLoopState('inactive');
    setPlayerLoopPoints(new Map());
  }, []);

  const toggleLoop = useCallback(() => {
    setIsLooping(prev => {
      const newValue = !prev;
      audioElements.forEach(audio => {
        audio.loop = newValue;
      });
      return newValue;
    });
  }, [audioElements]);

  // Helper function to update last start positions when seeking during playback
  const updateLastStartPositions = useCallback(() => {
    if (isPlaying) {
      const newStartPositions = new Map<number, number>();
      audioElements.forEach((audio, playerNumber) => {
        newStartPositions.set(playerNumber, audio.currentTime);
      });
      setLastStartPositions(newStartPositions);
      debugLog('Updated last start positions during playback', Object.fromEntries(newStartPositions));
    }
  }, [isPlaying, audioElements]);

  const forward = useCallback(async (seconds: number = 3) => {
    const reference = audioElements.get(selectedPlayer);
    if (!reference) return;
    const target = Math.min(reference.currentTime + seconds, reference.duration || Infinity);
    
    audioElements.forEach(audio => {
      audio.currentTime = target;
    });
    
    // On mobile, wait for seeks to complete
    if (isMobileDevice()) {
      await waitForAudioElementsReady(audioElements, target, 500);
    }
    
    // Update last start positions if playing
    updateLastStartPositions();
  }, [audioElements, selectedPlayer, updateLastStartPositions, waitForAudioElementsReady]);

  const rewind = useCallback(async (seconds: number = 3) => {
    const reference = audioElements.get(selectedPlayer);
    if (!reference) return;
    const target = Math.max(reference.currentTime - seconds, 0);
    
    audioElements.forEach(audio => {
      audio.currentTime = target;
    });
    
    // On mobile, wait for seeks to complete
    if (isMobileDevice()) {
      await waitForAudioElementsReady(audioElements, target, 500);
    }
    
    // Update last start positions if playing
    updateLastStartPositions();
  }, [audioElements, selectedPlayer, updateLastStartPositions, waitForAudioElementsReady]);

  const seekToTime = useCallback(async (time: number, syncPoints?: Map<number, number>, hasJumpedToSyncPoints?: boolean) => {
    const reference = audioElements.get(selectedPlayer);
    if (!reference) return;
    const target = Math.max(0, Math.min(time, reference.duration || Infinity));
    
    // If sync mode is active, respect sync offsets
    if (syncPoints && hasJumpedToSyncPoints && syncPoints.size > 1) {
      const referenceSyncPoint = syncPoints.get(selectedPlayer);
      if (referenceSyncPoint !== undefined) {
        const referenceOffset = target - referenceSyncPoint;
        
        audioElements.forEach((audio, playerNumber) => {
          const playerSyncPoint = syncPoints.get(playerNumber);
          if (playerSyncPoint !== undefined) {
            const displacedTime = playerSyncPoint + referenceOffset;
            const clampedTime = Math.max(0, Math.min(displacedTime, audio.duration || Infinity));
            audio.currentTime = clampedTime;
          } else {
            // For unsynced players, use the target time directly
            audio.currentTime = target;
          }
        });
      } else {
        // Fallback to setting all players to the same time
        audioElements.forEach(audio => {
          audio.currentTime = target;
        });
      }
    } else {
      // No sync mode, set all players to the same time
      audioElements.forEach(audio => {
        audio.currentTime = target;
      });
    }
    
    // On mobile, wait for seeks to complete
    if (isMobileDevice()) {
      await waitForAudioElementsReady(audioElements, target, 500);
    }
    
    // Update last start positions if playing
    updateLastStartPositions();
  }, [audioElements, selectedPlayer, updateLastStartPositions, waitForAudioElementsReady]);

  const rewindToLastStart = useCallback(async () => {
    const reference = audioElements.get(selectedPlayer);
    if (!reference) return;
    
    const referenceStartPosition = lastStartPositions.get(selectedPlayer);
    if (referenceStartPosition === undefined) {
      debugLog('No last start position found for selected player', { selectedPlayer });
      return;
    }
    
    const target = Math.max(0, Math.min(referenceStartPosition, reference.duration || Infinity));
    audioElements.forEach(audio => {
      audio.currentTime = target;
    });
    
    // On mobile, wait for seeks to complete
    if (isMobileDevice()) {
      await waitForAudioElementsReady(audioElements, target, 500);
    }
    
    debugLog('Rewound to last start position', { 
      selectedPlayer, 
      target, 
      lastStartPositions: Object.fromEntries(lastStartPositions) 
    });
  }, [audioElements, selectedPlayer, lastStartPositions, waitForAudioElementsReady]);

  // Helper function to initialize mobile player volumes
  const initializeMobilePlayerVolumes = useCallback(async () => {
    if (!isMobileDevice()) return;
    
    debugLog('Initializing mobile player volumes');
    
    try {
      // For each player, set the gain based on whether it's selected or not
      for (const playerNumber of audioElements.keys()) {
        const isPlayerSelected = playerNumber === selectedPlayer;
        const gainOffset = gainOffsetsRef.current.get(playerNumber) || 1;
        const targetVolume = isPlayerSelected ? gainOffset : 0;
        
        debugLog(`Setting initial gain for player ${playerNumber}`, { 
          isSelected: isPlayerSelected, 
          gainOffset, 
          targetVolume 
        });
        
        try {
          // Set gain immediately without fading
          await fadeMobileGain(playerNumber, targetVolume, 0);
        } catch (error) {
          console.warn(`Failed to set initial gain for player ${playerNumber}:`, error);
        }
      }
    } catch (error) {
      console.error('Failed to initialize mobile player volumes:', error);
    }
  }, [audioElements, selectedPlayer]);

  const setGainOffsets = useCallback((offsets: Map<number, number>) => {
    debugLog('Setting gain offsets', Object.fromEntries(offsets));
    
    // Store gains in ref
    offsets.forEach((_, playerNumber) => {
      gainOffsetsRef.current.set(playerNumber, offsets.get(playerNumber) || 1);
    });
    
    // Update state to trigger UI updates
    setGainOffsetsState(new Map(offsets));
    
    // Apply gains to all audio elements immediately
    offsets.forEach((_, playerNumber) => {
      const audio = audioElements.get(playerNumber);
      if (audio) {
        applyGainToAudio(audio, playerNumber);
      }
    });

    // Initialize mobile player volumes after gain calculations
    if (isMobileDevice()) {
      initializeMobilePlayerVolumes();
    }
  }, [audioElements, applyGainToAudio, initializeMobilePlayerVolumes]);

  const setGainOffsetsWithFileMapping = useCallback((offsets: Map<number, number>, fileOrder: Array<{id: string}>) => {
    debugLog('Setting gain offsets with file mapping', Object.fromEntries(offsets));
    debugLog('File order for mapping:', { fileIds: fileOrder.map(f => f.id) });
    
    // Clear and rebuild player number to file ID mapping
    playerNumberToFileIdRef.current.clear();
    
    // Store gains in ref and create mappings
    offsets.forEach((_, playerNumber) => {
      gainOffsetsRef.current.set(playerNumber, offsets.get(playerNumber) || 1);
    });
    
    // Store file ID to gain offset mapping and player number to file ID mapping
    fileOrder.forEach((file, index) => {
      const playerNumber = index + 1;
      const gainOffset = offsets.get(playerNumber) || 1;
      fileIdToGainOffsetRef.current.set(file.id, gainOffset);
      playerNumberToFileIdRef.current.set(playerNumber, file.id);
      
      // Store original gains using file ID as key for toggle functionality
      originalGainOffsetsRef.current.set(file.id, gainOffset);
      
      debugLog(`Mapped file ${file.id} (player ${playerNumber}) to gain offset ${gainOffset}`);
    });
    
    // Update state to trigger UI updates
    setGainOffsetsState(new Map(offsets));
    
    // Apply gains to all audio elements immediately
    offsets.forEach((_, playerNumber) => {
      const audio = audioElements.get(playerNumber);
      if (audio) {
        applyGainToAudio(audio, playerNumber);
      }
    });

    // Initialize mobile player volumes after gain calculations
    if (isMobileDevice()) {
      initializeMobilePlayerVolumes();
    }
  }, [audioElements, applyGainToAudio, initializeMobilePlayerVolumes]);

  const unregisterAudio = useCallback((id: number) => {
    const audio = audioElements.get(id);
    if (audio) {
      // Only remove if the audio element is actually invalid
      if (!audio.src || audio.readyState === 0) {
        audioElements.delete(id);
      }
    }
  }, [audioElements]);

  const toggleBlindTestMode = useCallback(() => {
    setIsBlindTestMode(prev => {
      const newValue = !prev;
      if (!prev && isPlaying) {
        pauseAll();
      } else if (prev && isPlaying) {
        stopAll();
      }
      // Set mode to 'basic' when enabling blind test mode
      if (newValue) {
        setBlindTestModeState('basic');
      } else {
        setBlindTestModeState(null);
      }
      return newValue;
    });
  }, [isPlaying, pauseAll, stopAll]);

  const setBlindTestMode = useCallback((mode: 'basic' | 'pick-fave' | 'guess-track' | null) => {
    setBlindTestModeState(mode);
  }, []);

  const remapGainOffsets = useCallback((newFileOrder: Array<{id: string}>) => {
    console.log('[remapGainOffsets] Starting gain offset remapping for reordered files');
    console.log('[remapGainOffsets] New file order:', newFileOrder.map(f => f.id));
    console.log('[remapGainOffsets] Current file-to-gain mapping:', Object.fromEntries(fileIdToGainOffsetRef.current));
    
    // Clear and rebuild player number to file ID mapping
    playerNumberToFileIdRef.current.clear();
    
    // Create new gain offsets map based on the new order
    const newGainOffsets = new Map<number, number>();
    
    // Create file ID to new player number mapping for mobile WebAudio remapping
    const fileIdToNewPlayerNumber = new Map<string, number>();
    
    // For each file in the new order, find its corresponding gain offset from the file ID mapping
    newFileOrder.forEach((file, index) => {
      const newPlayerNumber = index + 1;
      const gainOffset = fileIdToGainOffsetRef.current.get(file.id) || 1;
      newGainOffsets.set(newPlayerNumber, gainOffset);
      playerNumberToFileIdRef.current.set(newPlayerNumber, file.id);
      fileIdToNewPlayerNumber.set(file.id, newPlayerNumber);
      console.log(`[remapGainOffsets] Mapping file ${file.id} (new player ${newPlayerNumber}) to gain offset: ${gainOffset}`);
    });
    
    console.log('[remapGainOffsets] New gain offsets:', Object.fromEntries(newGainOffsets));
    
    // For mobile, remap WebAudio connections based on file IDs
    if (isMobileDevice()) {
      try {
        console.log('[remapGainOffsets] Remapping mobile WebAudio connections based on file IDs');
        remapMobileAudioConnections(fileIdToNewPlayerNumber);
      } catch (error) {
        console.error(`[remapGainOffsets] Failed to remap mobile audio connections:`, error);
      }
    }
    
    // Update the gain offsets
    setGainOffsets(newGainOffsets);
  }, [setGainOffsets]);

  const removePlayer = useCallback((id: number) => {
    // Don't allow removing the last player
    if (audioElements.size <= 1) {
      console.log('Cannot remove the last player');
      return;
    }

    console.log(`[removePlayer] Starting removal of player ${id}`);
    console.log(`[removePlayer] Current state:`, {
      selectedPlayer,
      audioElementsSize: audioElements.size,
      audioElementKeys: Array.from(audioElements.keys()),
      gainOffsetsRef: Object.fromEntries(gainOffsetsRef.current),
      fileIdToGainOffsetRef: Object.fromEntries(fileIdToGainOffsetRef.current),
      playerNumberToFileIdRef: Object.fromEntries(playerNumberToFileIdRef.current)
    });

    // Create a new audio elements map
    const newAudioElements = new Map<number, HTMLAudioElement>();
    const newGainOffsetsRef = new Map<number, number>();
    const newPlayerNumberToFileId = new Map<number, string>();
    
    // Keep track of the new index to assign to players
    let newIndex = 1;
    let newSelectedPlayer = selectedPlayer;
    let playerToSelect = Math.max(1, id - 1); // Default to selecting the player above the removed one

    console.log(`[removePlayer] Initial playerToSelect: ${playerToSelect}`);

    // Clean up mobile audio context references for the removed player
    if (isMobileDevice()) {
      cleanupMobileAudioForPlayer(id);
    }

    // Copy all audio elements except the one to remove
    // Reassign indices to be sequential starting from 1
    Array.from(audioElements.entries())
      .sort(([a], [b]) => naturalSortNumbers(a, b))
      .forEach(([oldIndex, audio]) => {
        if (oldIndex !== id) {
          console.log(`[removePlayer] Mapping oldIndex ${oldIndex} to newIndex ${newIndex}`);
          newAudioElements.set(newIndex, audio);
          // Get file ID for this player and look up gain offset by file ID
          // This ensures gain offsets are preserved correctly even when multiple players are deleted
          const fileId = playerNumberToFileIdRef.current.get(oldIndex);
          if (fileId) {
            newPlayerNumberToFileId.set(newIndex, fileId);
            const gain = fileIdToGainOffsetRef.current.get(fileId) || 1;
            console.log(`[removePlayer] oldIndex ${oldIndex} -> newIndex ${newIndex}, fileId: ${fileId}, gain from fileId: ${gain}`);
            newGainOffsetsRef.set(newIndex, gain);
          } else {
            // Fallback to old player-number-based lookup if file ID not found
            const gain = gainOffsetsRef.current.get(oldIndex) || 1;
            console.log(`[removePlayer] oldIndex ${oldIndex} -> newIndex ${newIndex}, NO FILE ID, gain from oldIndex: ${gain}`);
            newGainOffsetsRef.set(newIndex, gain);
          }
          if (oldIndex === selectedPlayer) {
            newSelectedPlayer = newIndex;
            console.log(`[removePlayer] Selected player ${oldIndex} remapped to ${newIndex}`);
          } else if (oldIndex === playerToSelect) {
            // Track the new index of the player we want to select
            playerToSelect = newIndex;
            console.log(`[removePlayer] PlayerToSelect ${oldIndex} remapped to ${newIndex}`);
          }
          newIndex++;
        }
      });

    // If the selected player was removed, select the player that was above it
    if (id === selectedPlayer) {
      newSelectedPlayer = playerToSelect;
      console.log(`[removePlayer] Selected player was removed, choosing playerToSelect: ${playerToSelect}`);
    }

    console.log(`[removePlayer] Final newSelectedPlayer: ${newSelectedPlayer}`);
    console.log(`[removePlayer] New audio elements:`, {
      newAudioElementsKeys: Array.from(newAudioElements.keys()),
      newGainOffsetsRef: Object.fromEntries(newGainOffsetsRef)
    });

    // Update the audio elements map
    audioElements.clear();
    newAudioElements.forEach((audio, index) => {
      audioElements.set(index, audio);
    });

    // Clean up and renumber wavesurferRefs to match the new player numbers
    const newWavesurferRefs = new Map<number, WaveSurfer>();
    Array.from(wavesurferRefs.current.entries())
      .sort(([a], [b]) => naturalSortNumbers(a, b))
      .forEach(([oldIndex, wavesurfer]) => {
        if (oldIndex !== id) {
          // Find the new index for this wavesurfer by matching its media element
          const mediaElement = wavesurfer.getMediaElement();
          if (mediaElement) {
            // Find which new player number this media element belongs to
            for (const [newPlayerNumber, audioElement] of newAudioElements.entries()) {
              if (audioElement === mediaElement) {
                newWavesurferRefs.set(newPlayerNumber, wavesurfer);
                break;
              }
            }
          }
        }
      });
    wavesurferRefs.current.clear();
    newWavesurferRefs.forEach((wavesurfer, index) => {
      wavesurferRefs.current.set(index, wavesurfer);
    });

    // Update gain offsets ref
    gainOffsetsRef.current = newGainOffsetsRef;

    // Update gain offsets state
    setGainOffsetsState(new Map(newGainOffsetsRef));

    // Clean up file ID mappings for removed player and update mapping
    const removedFileId = playerNumberToFileIdRef.current.get(id);
    if (removedFileId) {
      playerNumberToFileIdRef.current.delete(id);
      // Note: We keep the originalGainOffsetsRef entry in case the file is re-added
      console.log(`[removePlayer] Cleaned up file ID mapping for removed player ${id} (file ${removedFileId})`);
    }
    
    // Update player number to file ID mapping with the new mappings we built
    playerNumberToFileIdRef.current.clear();
    newPlayerNumberToFileId.forEach((fileId, newPlayerNumber) => {
      playerNumberToFileIdRef.current.set(newPlayerNumber, fileId);
      console.log(`[removePlayer] Rebuilt mapping: player ${newPlayerNumber} -> file ${fileId}`);
    });

    // For mobile, remap WebAudio connections based on file IDs
    if (isMobileDevice()) {
      try {
        // Create file ID to new player number mapping
        const fileIdToNewPlayerNumber = new Map<string, number>();
        newPlayerNumberToFileId.forEach((fileId, newPlayerNumber) => {
          fileIdToNewPlayerNumber.set(fileId, newPlayerNumber);
        });
        remapMobileAudioConnections(fileIdToNewPlayerNumber, removedFileId);
      } catch (error) {
        console.error(`[removePlayer] Failed to remap mobile audio connections:`, error);
      }
    }

    // Clean up blind test included players state
    setBlindTestIncludedPlayers(prev => {
      const newMap = new Map<number, boolean>();
      // Re-map the blind test inclusion state to new indices
      Array.from(audioElements.entries())
        .sort(([a], [b]) => naturalSortNumbers(a, b))
        .forEach(([oldIndex], newIndex) => {
          if (oldIndex !== id) {
            const isIncluded = prev.get(oldIndex) ?? true;
            newMap.set(newIndex + 1, isIncluded);
          }
        });
      return newMap;
    });

    // Update selected player
    console.log(`[removePlayer] Setting selected player to: ${newSelectedPlayer}`);
    setSelectedPlayer(newSelectedPlayer);
    
    // Apply proper volume settings to ALL players after removal
    setTimeout(() => {
      console.log(`[removePlayer] Timeout executing - applying volume settings`);
      console.log(`[removePlayer] Current selectedPlayer state: ${selectedPlayer}`);
      console.log(`[removePlayer] Target newSelectedPlayer: ${newSelectedPlayer}`);
      console.log(`[removePlayer] Current audioElements:`, Array.from(audioElements.keys()));
      
      // Set volumes for ALL players to ensure correct state
      audioElements.forEach((audio, playerNumber) => {
        const isTargetSelected = playerNumber === newSelectedPlayer;
        const gainOffset = gainOffsetsRef.current.get(playerNumber) || 1;
        const targetVolume = isTargetSelected ? gainOffset : 0;
        
        console.log(`[removePlayer] Setting volume for player ${playerNumber}:`, {
          isTargetSelected,
          gainOffset,
          targetVolume,
          previousVolume: audio.volume
        });
        
        audio.volume = Math.max(0, Math.min(1, targetVolume));
        
        console.log(`[removePlayer] Volume after setting for player ${playerNumber}: ${audio.volume}`);
      });
      
      // For mobile devices, ensure WebAudio gain nodes are properly updated
      if (isMobileDevice()) {
        console.log(`[removePlayer] Updating mobile gain nodes`);
        audioElements.forEach(async (audio, playerNumber) => {
          try {
            const isTargetSelected = playerNumber === newSelectedPlayer;
            const gainOffset = gainOffsetsRef.current.get(playerNumber) || 1;
            const targetVolume = isTargetSelected ? gainOffset : 0;
            
            console.log(`[removePlayer] Setting mobile gain for player ${playerNumber}:`, {
              isTargetSelected,
              gainOffset,
              targetVolume
            });
            
            await fadeMobileGain(playerNumber, targetVolume, 0);
          } catch (error) {
            console.warn(`Failed to set gain for player ${playerNumber} after removal:`, error);
          }
        });
      }
      
      // Focus management - find the player element and focus it
      try {
        const playerElement = document.querySelector(`[data-player-number="${newSelectedPlayer}"]`);
        if (playerElement) {
          // Force blur on any other element that might have focus
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          // Force focus on the player element
          (playerElement as HTMLElement).focus();
          console.log(`[removePlayer] Focused on player element ${newSelectedPlayer}`);
        } else {
          console.warn(`[removePlayer] Could not find player element for ${newSelectedPlayer}`);
        }
      } catch (error) {
        console.warn('Error setting focus on player element:', error);
      }
      
      // Final state check
      console.log(`[removePlayer] Final volumes:`, 
        Array.from(audioElements.entries()).map(([num, audio]) => `${num}: ${audio.volume}`)
      );
    }, 100); // Increased timeout to ensure DOM has updated
  }, [audioElements, selectedPlayer, unregisterAudio, stopAll]);

  const toggleRandomSwitching = useCallback(() => {
    setIsRandomSwitching(prev => {
      const newValue = !prev;
      localStorage.setItem('isRandomSwitching', String(newValue));
      return newValue;
    });
  }, []);

  const toggleBlindTestIncluded = useCallback((playerNumber: number) => {
    setBlindTestIncludedPlayers(prev => {
      const newMap = new Map(prev);
      // Default to included (true) if not set, so toggle to false
      // If false, toggle back to true
      const currentValue = newMap.get(playerNumber) ?? true;
      newMap.set(playerNumber, !currentValue);
      return newMap;
    });
  }, []);

  const toggleBlindTestIncludedForSelected = useCallback(() => {
    toggleBlindTestIncluded(selectedPlayer);
  }, [selectedPlayer, toggleBlindTestIncluded]);

  const toggleAllBlindTestIncluded = useCallback(() => {
    setBlindTestIncludedPlayers(prev => {
      const newMap = new Map(prev);
      const allPlayerNumbers = Array.from(audioElements.keys());
      
      debugLog('toggleAllBlindTestIncluded called', { 
        allPlayerNumbers, 
        currentState: Object.fromEntries(newMap) 
      });
      
      // Count how many are currently excluded
      let excludedCount = 0;
      let includedCount = 0;
      
      allPlayerNumbers.forEach(playerNum => {
        const isIncluded = newMap.get(playerNum) ?? true;
        if (isIncluded) {
          includedCount++;
        } else {
          excludedCount++;
        }
      });
      
      debugLog('Current counts', { includedCount, excludedCount, total: allPlayerNumbers.length });
      
      // If any are excluded, include all. If all are included, exclude all.
      const shouldIncludeAll = excludedCount > 0;
      
      debugLog('Action', { shouldIncludeAll });
      
      allPlayerNumbers.forEach(playerNum => {
        newMap.set(playerNum, shouldIncludeAll);
      });
      
      debugLog('Final state', { newState: Object.fromEntries(newMap) });
      
      return newMap;
    });
  }, [audioElements, debugLog]);

  const getPlayerLoopPoints = useCallback((playerNumber: number): PlayerLoopPoints | null => {
    return playerLoopPoints.get(playerNumber) || null;
  }, [playerLoopPoints]);

  const registerWaveSurfer = useCallback(async (playerNumber: number, wavesurfer: WaveSurfer) => {
    wavesurferRefs.current.set(playerNumber, wavesurfer);
    const wavesurferAny = wavesurfer as any;
    console.log(`[StereoMode] Registered WaveSurfer for player ${playerNumber}`, {
      hasBackend: !!wavesurferAny?.backend,
      hasAC: !!wavesurferAny?.backend?.ac,
      hasSource: !!wavesurferAny?.backend?.source,
      backendType: wavesurferAny?.backend?.constructor?.name
    });
    
    // Always connect WaveSurfer through dim gain nodes (even in stereo mode with no filter)
    // This ensures dim functionality works for all WaveSurfer instances
    if (!isMobileDevice()) {
      // Wait a bit for backend to be fully ready
      setTimeout(async () => {
        try {
          // Always connect to ensure dim gain nodes are created
          await connectWaveSurferToStereoMode(playerNumber, wavesurfer, stereoMode);
        } catch (error) {
          console.warn(`Failed to connect newly registered WaveSurfer ${playerNumber}:`, error);
        }
      }, 100);
    }
  }, [stereoMode, filterType]);

  const resetMono = useCallback(async () => {
    // Reset to stereo mode
    if (stereoMode !== 'stereo') {
      console.log('[StereoMode] Resetting to stereo');
      setStereoMode('stereo');
      await setMasterStereoMode('stereo');
      
      // Disconnect WaveSurfer instances from processing (desktop)
      if (!isMobileDevice()) {
        for (const [playerNumber, wavesurfer] of wavesurferRefs.current.entries()) {
          disconnectWaveSurferFromStereoMode(playerNumber, wavesurfer);
        }
      }
    }
  }, [stereoMode]);

  const toggleStereoMode = useCallback(async () => {
    // Cycle through modes: stereo → mono → side → stereo
    const modeOrder: StereoMode[] = ['stereo', 'mono', 'side'];
    const currentIndex = modeOrder.indexOf(stereoMode);
    // If somehow the mode is still invalid, default to stereo
    const safeIndex = currentIndex === -1 ? 0 : currentIndex;
    const nextIndex = (safeIndex + 1) % modeOrder.length;
    const nextMode = modeOrder[nextIndex];
    
    console.log(`[StereoMode] Cycling from ${stereoMode} to ${nextMode}`, {
      registeredWaveSurfers: Array.from(wavesurferRefs.current.keys()),
      isMobile: isMobileDevice()
    });
    
    setStereoMode(nextMode);
    
    // Set master stereo mode in Web Audio (for mobile)
    await setMasterStereoMode(nextMode);
    
    // For desktop: use WaveSurfer's audio graph
    if (!isMobileDevice()) {
      // Check if filter is enabled - if so, always route through processing
      const filterEnabled = filterType !== 'off';
      
      if (nextMode === 'stereo' && !filterEnabled) {
        // Disable processing: disconnect all WaveSurfer instances from processing
        console.log('[StereoMode] Disabling desktop processing - disconnecting WaveSurfer instances');
        for (const [playerNumber, wavesurfer] of wavesurferRefs.current.entries()) {
          disconnectWaveSurferFromStereoMode(playerNumber, wavesurfer);
        }
      } else {
        // Enable processing: connect all WaveSurfer instances to processing
        // (either for mono/sides mode, or for filter in stereo mode)
        console.log(`[StereoMode] Enabling desktop ${nextMode}${filterEnabled ? ' (filter enabled)' : ''} - connecting WaveSurfer instances to processing`);
        console.log('[StereoMode] Registered WaveSurfer instances:', Array.from(wavesurferRefs.current.keys()));
        
        for (const [playerNumber, wavesurfer] of wavesurferRefs.current.entries()) {
          const wavesurferAny = wavesurfer as any;
          console.log(`[StereoMode] Processing WaveSurfer ${playerNumber}`, {
            hasWavesurfer: !!wavesurfer,
            hasBackend: !!wavesurferAny?.backend,
            hasAC: !!wavesurferAny?.backend?.ac,
            hasSource: !!wavesurferAny?.backend?.source
          });
          try {
            await connectWaveSurferToStereoMode(playerNumber, wavesurfer, nextMode);
          } catch (error) {
            console.warn(`[StereoMode] Failed to connect WaveSurfer ${playerNumber} to ${nextMode}:`, error);
          }
        }
      }
    }
    // On mobile, the audio is already routed through Web Audio master bus, so processing works automatically
  }, [stereoMode, filterType]);

  // Legacy function for backward compatibility
  const toggleMono = useCallback(async () => {
    // If currently stereo, go to mono; otherwise go to stereo
    if (stereoMode === 'stereo') {
      // Set to mono
      const nextMode: StereoMode = 'mono';
      setStereoMode(nextMode);
      await setMasterStereoMode(nextMode);
      
      if (!isMobileDevice()) {
        for (const [playerNumber, wavesurfer] of wavesurferRefs.current.entries()) {
          try {
            await connectWaveSurferToStereoMode(playerNumber, wavesurfer, nextMode);
          } catch (error) {
            console.warn(`[Mono] Failed to connect WaveSurfer ${playerNumber} to mono:`, error);
          }
        }
      }
    } else {
      // Set to stereo
      const nextMode: StereoMode = 'stereo';
      setStereoMode(nextMode);
      await setMasterStereoMode(nextMode);
      
      if (!isMobileDevice()) {
        for (const [playerNumber, wavesurfer] of wavesurferRefs.current.entries()) {
          disconnectWaveSurferFromStereoMode(playerNumber, wavesurfer);
        }
      }
    }
  }, [stereoMode]);

  const resetGainForPlayer = useCallback(async (playerNumber: number) => {
    const currentGain = gainOffsetsRef.current.get(playerNumber) || 1;
    const fileId = playerNumberToFileIdRef.current.get(playerNumber);
    
    if (!fileId) {
      debugLog(`No file ID found for player ${playerNumber}, skipping toggle`);
      return;
    }
    
    const originalGain = originalGainOffsetsRef.current.get(fileId) || 1;
    
    // Toggle: if currently at unity gain (1.0), restore original; otherwise reset to unity
    const newGain = currentGain === 1 ? originalGain : 1;
    
    debugLog(`Toggling gain for player ${playerNumber} (file ${fileId})`, { currentGain, originalGain, newGain });
    
    // Update the gain offset ref
    gainOffsetsRef.current.set(playerNumber, newGain);
    
    // Update state to trigger UI updates
    setGainOffsetsState(prev => {
      const newMap = new Map(prev);
      newMap.set(playerNumber, newGain);
      return newMap;
    });
    
    // Apply gain to the audio element
    const audio = audioElements.get(playerNumber);
    if (audio) {
      applyGainToAudio(audio, playerNumber);
    }
    
    // Update mobile gain node if on mobile
    if (isMobileDevice()) {
      try {
        const isSelected = playerNumber === selectedPlayer;
        const targetVolume = isSelected ? newGain : 0;
        await fadeMobileGain(playerNumber, targetVolume, 0);
      } catch (error) {
        console.warn(`Failed to toggle mobile gain for player ${playerNumber}:`, error);
      }
    }
  }, [audioElements, applyGainToAudio, selectedPlayer]);

  const resetAllGains = useCallback(async () => {
    // Check if all players are currently at unity gain
    let allAtUnity = true;
    audioElements.forEach((_, playerNumber) => {
      const currentGain = gainOffsetsRef.current.get(playerNumber) || 1;
      if (currentGain !== 1) {
        allAtUnity = false;
      }
    });
    
    debugLog('Toggling all gains', { allAtUnity });
    
    // Toggle: if all at unity, restore originals; otherwise reset all to unity
    audioElements.forEach((_, playerNumber) => {
      const fileId = playerNumberToFileIdRef.current.get(playerNumber);
      if (!fileId) {
        debugLog(`No file ID found for player ${playerNumber}, skipping toggle`);
        return;
      }
      
      const originalGain = originalGainOffsetsRef.current.get(fileId) || 1;
      const newGain = allAtUnity ? originalGain : 1;
      gainOffsetsRef.current.set(playerNumber, newGain);
    });
    
    // Update state to trigger UI updates
    setGainOffsetsState(prev => {
      const newMap = new Map(prev);
      audioElements.forEach((_, playerNumber) => {
        const fileId = playerNumberToFileIdRef.current.get(playerNumber);
        if (fileId) {
          const originalGain = originalGainOffsetsRef.current.get(fileId) || 1;
          const newGain = allAtUnity ? originalGain : 1;
          newMap.set(playerNumber, newGain);
        }
      });
      return newMap;
    });
    
    // Apply gains to all audio elements
    audioElements.forEach((audio, playerNumber) => {
      applyGainToAudio(audio, playerNumber);
    });
    
    // Update mobile gain nodes if on mobile
    if (isMobileDevice()) {
      try {
        for (const playerNumber of audioElements.keys()) {
          const isSelected = playerNumber === selectedPlayer;
          const fileId = playerNumberToFileIdRef.current.get(playerNumber);
          if (fileId) {
            const originalGain = originalGainOffsetsRef.current.get(fileId) || 1;
            const newGain = allAtUnity ? originalGain : 1;
            const targetVolume = isSelected ? newGain : 0;
            await fadeMobileGain(playerNumber, targetVolume, 0);
          }
        }
      } catch (error) {
        console.warn('Failed to toggle mobile gains:', error);
      }
    }
  }, [audioElements, applyGainToAudio, selectedPlayer]);

  const handleSetMasterFilter = useCallback(async (
    type: FilterType, 
    frequency: number, 
    q: number, 
    slope: FilterSlope = 12, 
    frequencyLow: number = 500, 
    frequencyHigh: number = 2000,
    inputGain: number = 1.0
  ) => {
    try {
      await setMasterFilter(type, frequency, q, slope, frequencyLow, frequencyHigh, inputGain);
      setFilterType(type);
      setFilterFrequency(frequency);
      setFilterQ(q);
      setFilterSlope(slope);
      setFilterFrequencyLow(frequencyLow);
      setFilterFrequencyHigh(frequencyHigh);
      setFilterInputGain(inputGain);
      
      // Store last active filter settings when filter is turned on
      if (type !== 'off') {
        setLastActiveFilter({
          type,
          frequency,
          q,
          slope,
          frequencyLow,
          frequencyHigh,
          inputGain
        });
      }
      
      debugLog('Master filter set', { type, frequency, q, slope, frequencyLow, frequencyHigh, inputGain });
      
      // Update or reconnect all WaveSurfer instances
      // Try fast parameter update first, fall back to full reconnect if needed
      if (!isMobileDevice()) {
        for (const [playerNumber, wavesurfer] of wavesurferRefs.current.entries()) {
          try {
            // Try fast parameter update first
            const updated = updateWaveSurferFilterParameters(playerNumber, stereoMode);
            if (!updated) {
              // Need full reconnect (type/slope changed or no existing nodes)
              await connectWaveSurferToStereoMode(playerNumber, wavesurfer, stereoMode);
            }
          } catch (error) {
            console.warn(`Failed to reconnect WaveSurfer ${playerNumber} after filter change:`, error);
          }
        }
      }
    } catch (error) {
      console.error('Failed to set master filter:', error);
    }
  }, [stereoMode]);
  
  const toggleMasterFilter = useCallback(async () => {
    if (filterType === 'off') {
      // Turn on filter with last active settings, or default if none
      if (lastActiveFilter) {
        await handleSetMasterFilter(
          lastActiveFilter.type,
          lastActiveFilter.frequency,
          lastActiveFilter.q,
          lastActiveFilter.slope,
          lastActiveFilter.frequencyLow,
          lastActiveFilter.frequencyHigh,
          lastActiveFilter.inputGain
        );
      } else {
        // Default to highpass at 1000Hz if no previous settings
        await handleSetMasterFilter('highpass', 1000, 0.707, 12, 500, 2000, 1.0);
      }
    } else {
      // Turn off filter, preserving current settings
      await handleSetMasterFilter('off', filterFrequency, filterQ, filterSlope, filterFrequencyLow, filterFrequencyHigh, filterInputGain);
    }
  }, [filterType, filterFrequency, filterQ, filterSlope, filterFrequencyLow, filterFrequencyHigh, filterInputGain, lastActiveFilter, handleSetMasterFilter]);

  const handleResetMasterFilter = useCallback(async () => {
    try {
      await setMasterFilter('off', 1000, 1.0, 12, 500, 2000, 1.0);
      setFilterType('off');
      setFilterFrequency(1000);
      setFilterQ(1.0);
      setFilterSlope(12);
      setFilterFrequencyLow(500);
      setFilterFrequencyHigh(2000);
      setFilterInputGain(1.0);
      debugLog('Master filter reset');
    } catch (error) {
      console.error('Failed to reset master filter:', error);
    }
  }, []);

  const handleToggleMasterDim = useCallback(async () => {
    try {
      console.log('[AudioContext] Toggling master dim, current state:', isDimmed);
      const newDimState = await toggleMasterDim();
      setIsDimmed(newDimState);
      console.log('[AudioContext] Master dim toggled, new state:', newDimState);
      
      // Reconnect all desktop WaveSurfer instances to ensure they route through dim gain nodes
      if (!isMobileDevice()) {
        console.log('[AudioContext] Reconnecting all WaveSurfer instances to ensure dim gain node routing');
        for (const [playerNumber, wavesurfer] of wavesurferRefs.current.entries()) {
          try {
            await connectWaveSurferToStereoMode(playerNumber, wavesurfer, stereoMode);
          } catch (error) {
            console.warn(`[AudioContext] Failed to reconnect WaveSurfer ${playerNumber} after dim toggle:`, error);
          }
        }
      }
      
      debugLog(`Master dim ${newDimState ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to toggle master dim:', error);
    }
  }, [isDimmed, stereoMode]);

  // Initialize dim state from the audio manager
  useEffect(() => {
    setIsDimmed(getMasterDimState());
  }, []);

  // Waveform synchronization function
  const synchronizeWaveforms = useCallback(async (referencePlayer: number): Promise<Map<number, number> | null> => {
    if (wavesurferRefs.current.size < 2) {
      console.warn('[AudioContext] Need at least 2 players to synchronize');
      return null;
    }

    if (!wavesurferRefs.current.has(referencePlayer)) {
      console.warn(`[AudioContext] Reference player ${referencePlayer} not found`);
      return null;
    }

    console.log(`[AudioContext] Starting waveform synchronization with reference player ${referencePlayer}`);
    
    try {
      const syncOffsets = await synchronizeAllWaveforms(wavesurferRefs.current, referencePlayer);
      console.log('[AudioContext] Synchronization complete', Object.fromEntries(syncOffsets));
      return syncOffsets;
    } catch (error) {
      console.error('[AudioContext] Waveform synchronization failed:', error);
      return null;
    }
  }, []);

  const value = {
    isPlaying,
    isLooping,
    currentTime,
    duration,
    selectedPlayer,
    gainOffsets,
    isBlindTestMode,
    blindTestMode,
    blindTestIncludedPlayers,
    loopStart: loopStartTime,
    loopEnd: loopEndTime,
    loopState,
    playerLoopPoints,
    lastStartPositions,
    setLastStartPositions,
    isRandomSwitching,
    playAll,
    pauseAll,
    stopAll,
    toggleLoop,
    forward,
    rewind,
    rewindToLastStart,
    seekToTime,
    selectPlayer,
    setGainOffsets,
    setGainOffsetsWithFileMapping,
    resetGainForPlayer,
    resetAllGains,
    toggleBlindTestMode,
    setBlindTestMode,
    toggleBlindTestIncluded,
    toggleBlindTestIncludedForSelected,
    toggleAllBlindTestIncluded,
    setLoopStart,
    setLoopEnd,
    disableLoop,
    toggleRandomSwitching,
    audioElements,
    registerAudio,
    unregisterAudio,
    removePlayer,
    remapGainOffsets,
    getPlayerLoopPoints,
    stereoMode,
    toggleStereoMode,
    isMono,
    toggleMono,
    resetMono,
    registerWaveSurfer,
    filterType,
    filterFrequency,
    filterQ,
    filterSlope,
    filterFrequencyLow,
    filterFrequencyHigh,
    filterInputGain,
    setMasterFilter: handleSetMasterFilter,
    resetMasterFilter: handleResetMasterFilter,
    toggleMasterFilter,
    hasFilterBeenSet: lastActiveFilter !== null,
    isDimmed,
    toggleMasterDim: handleToggleMasterDim,
    synchronizeWaveforms,
    isSpinning,
    triggerSpinAnimation,
    blindTestAllowRepeats,
    setBlindTestAllowRepeats,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};

export const useAudioContext = () => {
  const context = useContext(AudioContext);
  if (context === undefined) {
    // During hot reload, context can be temporarily undefined
    // In development, provide a more helpful error message
    const errorMessage = process.env.NODE_ENV === 'development'
      ? 'useAudioContext must be used within an AudioProvider (this may be a hot reload issue - the app should recover automatically)'
      : 'useAudioContext must be used within an AudioProvider';
    throw new Error(errorMessage);
  }
  return context;
}; 